/**
 * Helpers for the Intune worst-battery-devices triage section of the item panel.
 * Pure functions only, so they can be tested without rendering anything.
 */

export interface SwapTicket {
    summary: string;
    description: string;
}

export interface SwapTicketDevice {
    device_id: string | null;
    device_name: string | null;
    model: string | null;
    max_capacity_percentage: number | null;
    estimated_runtime_minutes: number | null;
    battery_age_days: number | null;
    full_battery_drain_count: number | null;
    health_status: string | null;
    /** From the hardware-asset index; null if no asset record matched this device. */
    serial_number?: string | null;
}

const deviceLabel = (device: SwapTicketDevice) =>
    device.device_name ?? device.device_id ?? "Unknown device";

/**
 * The hardware swap intake checklist for one device. Only the fault description, office address, issue
 * start date/time, prior-incident/service-request references, impacted-device-type checkbox and
 * troubleshooting notes are left blank: none of them are available from the battery index or the
 * hardware-asset index, so they are for the engineer to fill in. The serial number is filled in when the
 * asset index has it; the swap answer is always "Yes" since this is a swap request.
 */
const swapChecklist = (device: SwapTicketDevice) =>
    [
        `• Device: ${deviceLabel(device)}`,
        "",
        "⦁ Describe the fault and hardware defects:",
        "",
        "",
        "⦁ User's office address:",
        "",
        "",
        "⦁ When has the issue started?",
        "Date:",
        "Time:",
        "",
        "⦁ Did the caller report the same issue in another Incident?",
        "[ ] Yes (Reference: XXX)",
        "[ ] No",
        "",
        "⦁ Any related hardware Service Requests?",
        "[ ] Yes (Reference: XXX)",
        "[ ] No",
        "",
        "⦁ Select the impacted device:",
        "[ ] Standard Laptop",
        "[ ] Tablet PC",
        "[ ] Convertible PC",
        "[ ] Power Device",
        "[ ] Monitor",
        "[ ] Docking Station",
        "",
        `⦁ Device's Serial Number: ${device.serial_number ?? ""}`,
        "",
        "⦁ Does the device need swapping?",
        "(No swap available for peripherals)",
        "[x] Yes",
        "[ ] No",
        "",
        "⦁ Capture the performed troubleshooting:",
        "",
        "",
    ].join("\n");

/**
 * The text of the ServiceNow hardware swap ticket for the devices the engineer marked "swap", and what the
 * ignored ones are: one intake checklist per swapped device (see swapChecklist), since each checklist is
 * written for a single device and one incident can cover several.
 */
export function buildSwapTicket(
    proposalId: string,
    swap: SwapTicketDevice[],
    ignored: SwapTicketDevice[]
): SwapTicket {
    const count = swap.length;
    return {
        summary: `AIDWOps: battery swap for ${count} ${count === 1 ? "device" : "devices"}`,
        description: [
            `AIDWOps Intune battery health proposal ${proposalId} (triage batch)`,
            "Raised by: aidwops (detector)",
            "Purpose: physical battery swap for devices with critical capacity degradation.",
            "",
            `--- STAGED FOR SERVICENOW SWAP (${count} ${count === 1 ? "device" : "devices"}) ---`,
            count > 0
                ? swap.map(swapChecklist).join("\n\n")
                : "No devices selected for swap.",
            "",
            `--- IGNORED IN THIS BATCH (${ignored.length}) ---`,
            ignored.length > 0
                ? ignored
                      .map((device) => `• ${deviceLabel(device)} - not part of this request`)
                      .join("\n")
                : "None.",
            "",
            "This ticket is for the devices above only. Intune is not changed.",
        ].join("\n"),
    };
}

/** How worn a battery is. Below 40 % of its design capacity is critical. */
export function batteryBand(capacity: number | null): "critical" | "degraded" | "unknown" {
    if (capacity === null || Number.isNaN(capacity)) {
        return "unknown";
    }
    return capacity < 40 ? "critical" : "degraded";
}

export function formatRuntime(minutes: number | null): string {
    if (minutes === null || Number.isNaN(minutes)) {
        return "n/a";
    }
    const whole = Math.round(minutes);
    if (whole < 60) {
        return `${whole} min`;
    }
    const hours = Math.floor(whole / 60);
    const rest = whole % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export function formatDays(days: number | null): string {
    if (days === null || Number.isNaN(days)) {
        return "n/a";
    }
    return `${days} ${days === 1 ? "day" : "days"}`;
}
